import cn from 'classnames'
import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CheckCircleIcon} from '@/components/common/icons/CheckCircleIcon'
import {CloseCircleIcon} from '@/components/common/icons/CloseCircleIcon'
import {useTranslation} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {TipItemCategoryType} from './types'

type TipItemProps = {
  category: TipItemCategoryType
}

export const TipItem: FC<TipItemProps> = ({category: {title, description, examples}}) => {
  const {t} = useTranslation()

  return (
    <>
      <div className="tips">
        <Text fontWeight={TextFontWeight.bold}>{t(title)}</Text>
        <div className="description">
          <Text variant={TextVariant.text14}>{t(description)}</Text>
        </div>
        {examples.map(({badExample, goodExample}, i) => (
          <div key={i} className={cn('row', !badExample && 'without-bad-example')}>
            <div className="example">
              {badExample && (
                <>
                  {badExample.imageSrc && badExample.imageAlt && (
                    <img src={badExample.imageSrc} alt={badExample.imageAlt} className="image" />
                  )}
                  <div className="title">
                    <div className="icon">
                      <CloseCircleIcon size={IconSize.medium} />
                    </div>
                    <Text variant={TextVariant.text14}>{t(badExample.title)}</Text>
                  </div>
                </>
              )}
            </div>
            <div className="example">
              {goodExample.imageSrc && goodExample.imageAlt && (
                <img src={goodExample.imageSrc} alt={goodExample.imageAlt} className="image" />
              )}
              <div className="title">
                <div className="icon">
                  <CheckCircleIcon
                    checkColor={theme.palette.success.main}
                    backgroundColor={theme.palette.success.light}
                    size={IconSize.medium}
                  />
                </div>
                <Text variant={TextVariant.text14}>{t(goodExample.title)}</Text>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .tips + .tips {
          margin-top: ${theme.spacing[5]};
        }
        .description {
          margin-top: ${theme.spacing[1]};
        }
        .row {
          display: flex;
          margin-top: ${theme.spacing[4]};
        }
        .row + .row {
          margin-top: ${theme.spacing[2]};
        }
        .example {
          flex: 1;
        }
        .title {
          display: flex;
        }
        .example + .example {
          margin-left: ${theme.spacing[4]};
        }
        .icon {
          margin-right: ${theme.spacing[2.5]};
        }
        .image {
          width: 100%;
          height: auto;
          max-height: 220px;
          min-height: 220px;
          object-fit: contain;
          margin-bottom: ${theme.spacing[3]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .row {
            display: block;
          }
          .row + .row:not(.without-bad-example) {
            margin-top: ${theme.spacing[4]};
          }
          .example + .example {
            margin-left: 0;
            margin-top: ${theme.spacing[2]};
          }
          .without-bad-example .example {
            margin-top: 0;
          }
          .example + .example .image {
            margin-top: ${theme.spacing[3.5]};
          }
          .image {
            min-height: inherit;
          }
        }
      `}</style>
    </>
  )
}
